import { useCallback, useEffect, useRef, useState } from "react";
import { Music2, Pause, Play, SkipForward } from "lucide-react";
import axios from "axios";
import { API, getOwner } from "@/lib/ownerApi";
import OwnerMusicControls from "@/components/site/OwnerMusicControls";

const PAUSED_KEY = "elt-music-paused";
const FADE_IN_MS = 2000;
const FADE_OUT_MS = 700;

export default function MusicPlayer({ volume: VOLUME = 0.4, ownerControls = true }) {
  const audioRef = useRef(null);
  const rootRef = useRef(null);
  const keepPlayingRef = useRef(false);
  const fadeRef = useRef(null);
  const videoSoundRef = useRef(false);
  const [songs, setSongs] = useState([]);
  const [index, setIndex] = useState(0);
  const [owner, setOwner] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const hasSongs = songs.length > 0;
  const song = hasSongs ? songs[Math.min(index, songs.length - 1)] : null;

  // Smoothly ramps volume; resolves false if a newer fade interrupts it
  const fadeTo = useCallback((target, ms) => new Promise((resolve) => {
    const a = audioRef.current;
    if (fadeRef.current) { cancelAnimationFrame(fadeRef.current.raf); fadeRef.current.resolve(false); }
    if (!a) return resolve(false);
    const from = a.volume, t0 = performance.now();
    const job = { resolve, target };
    fadeRef.current = job;
    const step = (now) => {
      const k = Math.min(1, (now - t0) / ms);
      a.volume = from + (target - from) * (k * k * (3 - 2 * k));
      if (k < 1) job.raf = requestAnimationFrame(step);
      else { fadeRef.current = null; resolve(true); }
    };
    job.raf = requestAnimationFrame(step);
  }), []);

  const play = useCallback(() => {
    const a = audioRef.current;
    if (!a) return Promise.reject();
    if (a.paused) a.volume = 0;
    return a.play().then(() => { fadeTo(VOLUME, FADE_IN_MS); });
  }, [fadeTo, VOLUME]);

  const fadeOutAndPause = useCallback(async () => {
    const a = audioRef.current;
    if (!a || a.paused) return;
    if (await fadeTo(0, FADE_OUT_MS)) a.pause();
  }, [fadeTo]);

  useEffect(() => {
    axios.get(`${API}/music`).then(({ data }) => setSongs(data.songs)).catch(() => {});
    if (ownerControls) getOwner().then(setOwner);
  }, [ownerControls]);

  // Browsers block sound until the visitor interacts, so we start on their first tap/click/key
  useEffect(() => {
    if (!hasSongs || sessionStorage.getItem(PAUSED_KEY)) return;
    let armed = false;
    const events = ["pointerdown", "keydown", "touchstart"];
    const disarm = () => armed && events.forEach((ev) => window.removeEventListener(ev, start, true));
    function start(e) {
      // One-shot: after the first interaction anywhere, the player's own buttons take over
      disarm();
      armed = false;
      setWaiting(false);
      const a = audioRef.current;
      if (rootRef.current?.contains(e.target) || e.target.closest?.("[data-video-sound]") || !a?.paused || sessionStorage.getItem(PAUSED_KEY)) return;
      play().then(() => {
        setWaiting(false);
        if (videoSoundRef.current) fadeOutAndPause(); // a film's sound came on while the music was starting
      }).catch(() => {});
    }
    play()
      .then(() => setWaiting(false))
      .catch(() => {
        setWaiting(true);
        armed = true;
        events.forEach((ev) => window.addEventListener(ev, start, true));
      });
    return disarm;
  }, [hasSongs, play, fadeOutAndPause]);

  // When the track changes (skip or song ended), keep playing if music was already on
  useEffect(() => {
    if (keepPlayingRef.current) play().catch(() => {});
  }, [song?.id, play]);

  useEffect(() => {
    const onVideoSound = (e) => {
      videoSoundRef.current = !!e.detail?.sound;
      if (e.detail?.sound) fadeOutAndPause();
    };
    window.addEventListener("tribute-video-sound", onVideoSound);
    return () => window.removeEventListener("tribute-video-sound", onVideoSound);
  }, [fadeOutAndPause]);

  const advance = async (fadeOut) => {
    const a = audioRef.current;
    keepPlayingRef.current = true;
    sessionStorage.removeItem(PAUSED_KEY);
    if (fadeOut && a && !a.paused && !(await fadeTo(0, FADE_OUT_MS))) return;
    if (songs.length === 1 && a) {
      a.currentTime = 0;
      play().catch(() => {});
    } else {
      if (a) a.volume = 0;
      setIndex((i) => (i + 1) % songs.length);
    }
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    const fadingOut = !a.paused && fadeRef.current?.target === 0;
    if (fadingOut) { // tapped play again mid-fade-out: bring it back up
      sessionStorage.removeItem(PAUSED_KEY);
      fadeTo(VOLUME, FADE_IN_MS / 2);
    } else if (a.paused) {
      sessionStorage.removeItem(PAUSED_KEY);
      play().then(() => setWaiting(false)).catch(() => {});
    } else {
      sessionStorage.setItem(PAUSED_KEY, "1");
      fadeOutAndPause();
    }
  };

  const onSongsChange = (list) => {
    const currentId = song?.id;
    setSongs(list);
    const keep = list.findIndex((s) => s.id === currentId);
    setIndex(keep >= 0 ? keep : Math.max(0, Math.min(index, list.length - 1)));
  };

  if (!hasSongs && !owner) return null;

  return (
    <div ref={rootRef} data-testid="music-player" className="fixed bottom-4 left-4 z-40 max-w-[calc(100vw-2rem)]">
      {owner && <OwnerMusicControls songs={songs} currentId={song?.id} onSongsChange={onSongsChange} onSignOut={() => setOwner(false)} />}
      {song && (
        <div className="flex items-center gap-3 rounded-full border border-[rgba(212,175,55,0.35)] bg-[rgba(14,13,11,0.85)] backdrop-blur-md pl-2 pr-2 py-2 shadow-lg shadow-black/40">
          <audio
            ref={audioRef}
            data-testid="music-audio"
            src={`${process.env.REACT_APP_BACKEND_URL}${song.url}`}
            loop={songs.length === 1}
            preload="auto"
            onPlay={() => { setPlaying(true); keepPlayingRef.current = true; }}
            onPause={(e) => { setPlaying(false); if (!e.currentTarget.ended) keepPlayingRef.current = false; }}
            onEnded={() => advance(false)}
          />
          <button
            data-testid="music-toggle-button"
            onClick={toggle}
            aria-label={playing ? "Pause music" : "Play music"}
            className="h-9 w-9 shrink-0 rounded-full bg-[#D4AF37] text-[#0E0D0B] inline-flex items-center justify-center hover:bg-[#E6C65A] transition-colors"
          >
            {playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
          </button>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#C5A55A]/80 flex items-center gap-1.5">
              {playing ? <span className="music-bars" aria-hidden="true"><i /><i /><i /></span> : <Music2 size={11} />}
              {playing ? "Now playing" : waiting ? "Tap anywhere for music" : "Music paused"}
              {songs.length > 1 && <span data-testid="music-position" className="text-[#A0988E] tracking-normal normal-case">· {index + 1}/{songs.length}</span>}
            </div>
            <div data-testid="music-title" className="serif text-[15px] leading-tight text-[#F5F0E8] truncate max-w-[170px] sm:max-w-[240px]">{song.title}</div>
            {song.artist && (
              <div data-testid="music-artist" className="text-[12px] leading-tight text-[#A0988E] truncate max-w-[170px] sm:max-w-[240px]">{song.artist}</div>
            )}
          </div>
          {songs.length > 1 && (
            <button
              data-testid="music-skip-button"
              onClick={() => advance(true)}
              aria-label="Next song"
              className="h-9 w-9 shrink-0 rounded-full border border-[rgba(212,175,55,0.35)] text-[#D4AF37] inline-flex items-center justify-center hover:bg-[rgba(212,175,55,0.12)] transition-colors"
            >
              <SkipForward size={15} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
